import { FilterResponse, FiltrationType } from '@fdm/shared';
import moment, { Moment } from 'moment';
import { utcToLocal } from './date';

export function filterTable(
  data: any[],
  key: string,
  filterRes: FilterResponse | null,
) {
  if (!filterRes) {
    return data;
  }
  
  return data.filter((item) => {
    const isDate = filterRes.type === 'date';
    const filterType = filterRes.filter;
    const rawColumnValue = (item as any)?.[key];

    const columnDateValue = isDate
      ? utcToLocal(rawColumnValue)?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      : null;
    const filterDateValue = isDate
      ? utcToLocal(new Date(filterRes.value))?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      : null;

    const columnStringValue = !isDate && rawColumnValue != null
      ? String(rawColumnValue).toLowerCase()
      : null;
    const filterStringValue = !isDate && filterRes.value != null
      ? String(filterRes.value).toLowerCase()
      : null;

    if (isDate) {
      if (!columnDateValue) {
        return false;
      }

      switch (filterType) {
        case FiltrationType.ON:
          return !!filterDateValue && (columnDateValue as Moment).isSame(filterDateValue, 'day');

        case FiltrationType.ON_OR_AFTER:
          return !!filterDateValue && (columnDateValue as Moment).isSameOrAfter(filterDateValue, 'day');

        case FiltrationType.ON_OR_BEFORE:
          return !!filterDateValue && (columnDateValue as Moment).isSameOrBefore(filterDateValue, 'day');

        case FiltrationType.TODAY: {
          const today = moment();
          return (columnDateValue as Moment).isSame(today, 'day');
        }

        case FiltrationType.YESTERDAY: {
          const yesterday = moment().subtract(1, 'day');
          return (columnDateValue as Moment).isSame(yesterday, 'day');
        }

        case FiltrationType.TOMORROW: {
          const tomorrow = moment().add(1, 'day');
          return (columnDateValue as Moment).isSame(tomorrow, 'day');
        }

        case FiltrationType.THIS_WEEK: {
          const weekStart = moment().startOf('week');
          const weekEnd = moment().endOf('week');
          return (
            (columnDateValue as Moment).isSameOrAfter(weekStart, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(weekEnd, 'day')
          );
        }

        case FiltrationType.THIS_MONTH: {
          const monthStart = moment().startOf('month');
          const monthEnd = moment().endOf('month');
          return (
            (columnDateValue as Moment).isSameOrAfter(monthStart, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(monthEnd, 'day')
          );
        }

        case FiltrationType.THIS_YEAR: {
          const yearStart = moment().startOf('year');
          const yearEnd = moment().endOf('year');
          return (
            (columnDateValue as Moment).isSameOrAfter(yearStart, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(yearEnd, 'day')
          );
        }

        case FiltrationType.NEXT_MONTH: {
          const start = moment().add(1, 'month').startOf('month');
          const end = moment().add(1, 'month').endOf('month');
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        case FiltrationType.NEXT_YEAR: {
          const start = moment().add(1, 'year').startOf('year');
          const end = moment().add(1, 'year').endOf('year');
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        case FiltrationType.LAST_WEEK: {
          const start = moment().subtract(1, 'week').startOf('week');
          const end = moment().subtract(1, 'week').endOf('week');
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        case FiltrationType.LAST_7_DAYS: {
          const start = moment().subtract(7, 'days');
          const end = moment();
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        case FiltrationType.LAST_MONTH: {
          const start = moment().subtract(1, 'month').startOf('month');
          const end = moment().subtract(1, 'month').endOf('month');
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        case FiltrationType.LAST_YEAR: {
          const start = moment().subtract(1, 'year').startOf('year');
          const end = moment().subtract(1, 'year').endOf('year');
          return (
            (columnDateValue as Moment).isSameOrAfter(start, 'day') &&
            (columnDateValue as Moment).isSameOrBefore(end, 'day')
          );
        }

        default:
          return true;
      }
    }

    switch (filterType) {
      case FiltrationType.EQUALS:
        return columnStringValue === filterStringValue || !Boolean(filterStringValue);

      case FiltrationType.DOES_NOT_EQUAL:
        return columnStringValue !== filterStringValue || !Boolean(filterStringValue);

      case FiltrationType.CONTAINS_DATA:
        return columnStringValue != null && columnStringValue.trim() !== '';

      case FiltrationType.DOES_NOT_CONTAIN_DATA:
        return columnStringValue == null || columnStringValue.trim() === '';

      case FiltrationType.CONTAINS:
        return columnStringValue?.includes(filterStringValue ?? '') || !Boolean(filterStringValue);

      case FiltrationType.DOES_NOT_CONTAIN:
        return !columnStringValue?.includes(filterStringValue ?? '') || !Boolean(filterStringValue);

      case FiltrationType.BEGINS_WITH:
        return columnStringValue?.startsWith(filterStringValue ?? '') || !Boolean(filterStringValue);

      case FiltrationType.DOES_NOT_BEGIN_WITH:
        return !columnStringValue?.startsWith(filterStringValue ?? '') || !Boolean(filterStringValue);

      case FiltrationType.ENDS_WITH:
        return columnStringValue?.endsWith(filterStringValue ?? '') || !Boolean(filterStringValue);

      case FiltrationType.DOES_NOT_END_WITH:
        return !columnStringValue?.endsWith(filterStringValue ?? '') || !Boolean(filterStringValue);

      default:
        return true;
    }
  });
}
